"use client";

import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { HOME_SECTIONS } from "@/lib/home-sections";

interface PageContentsSectionProps {
    className?: string;
}

// Ids and icons are stable — titles come from translations, and the order is
// owned by HOME_SECTIONS so this list cannot disagree with the page.
const SECTION_CONFIG = HOME_SECTIONS;

/** Matches the md: breakpoint the layout classes switch on. */
const MOBILE_BREAKPOINT_PX = 768;
/** How far past the hero's bottom edge the nav appears. */
const HERO_CLEARANCE_PX = 200;
/** Clearance kept between the nav and the top of the footer. */
const FOOTER_GAP_PX = 24;
/** Offset applied when scrolling to a section, to clear the fixed header. */
const SCROLL_OFFSET_PX = 80;

export function PageContentsSection({ className }: PageContentsSectionProps) {
    const t = useTranslations('pageContents');
    const [activeSection, setActiveSection] = useState("hero");
    const [isVisible, setIsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const navRef = useRef<HTMLElement>(null);

    useEffect(() => {
        let frame = 0;

        const evaluate = () => {
            frame = 0;

            // Appear once the hero has scrolled past. Measured from the live
            // rect rather than offsetTop, which is relative to the nearest
            // positioned ancestor and silently wrong if one is ever added.
            const hero = document.getElementById("hero");
            let shouldBeVisible = hero
                ? hero.getBoundingClientRect().bottom <= HERO_CLEARANCE_PX
                : false;

            // Retreat before the footer reaches the nav. This is deliberately
            // not conditioned on the breakpoint: the desktop layout sits
            // centred on the right edge and covered the "Return & Exchange
            // Policy" link, while the mobile layout sits at the bottom and
            // covered the social buttons. Comparing the footer against the
            // nav's own measured box handles both without hardcoding either
            // layout's geometry.
            const footer = document.querySelector('footer[role="contentinfo"]');
            if (shouldBeVisible && footer && navRef.current) {
                const navBottom = navRef.current.getBoundingClientRect().bottom;
                shouldBeVisible =
                    footer.getBoundingClientRect().top > navBottom + FOOTER_GAP_PX;
            }

            setIsVisible(shouldBeVisible);

            const current = SECTION_CONFIG
                .map(({ id }) => ({ id, element: document.getElementById(id) }))
                .find(({ element }) => {
                    if (!element) return false;
                    const rect = element.getBoundingClientRect();
                    return rect.top <= 100 && rect.bottom >= 100;
                });

            if (current) setActiveSection(current.id);
        };

        // Scroll fires far more often than the browser paints, and this reads
        // layout for every section — coalesce to one read per frame.
        const onScroll = () => {
            if (frame === 0) frame = requestAnimationFrame(evaluate);
        };

        const onResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT_PX);
            onScroll();
        };

        onResize();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            if (frame !== 0) cancelAnimationFrame(frame);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll);
        };
        // Intentionally empty: every value this reads is fetched live from the
        // DOM or a ref. Closing over isMobile here is what previously froze the
        // footer check at its mount-time value of false, disabling it entirely.
    }, []);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (!element) return;

        const top =
            element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX;
        const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        window.scrollTo({
            top,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
    };

    // Kept mounted rather than returning null so it can fade, and so its box
    // stays measurable for the footer check above while it is hidden.
    return (
        <nav
            ref={navRef}
            aria-label={t('landmarkLabel')}
            aria-hidden={!isVisible}
            className={cn(
                "fixed z-50 transition-opacity duration-300",
                isVisible ? "opacity-100" : "opacity-0 pointer-events-none",
                isMobile
                    ? "bottom-4 left-1/2 -translate-x-1/2" // Mobile: horizontal at bottom
                    : "right-4 md:right-8 top-1/2 -translate-y-1/2", // Desktop: vertical on right
                className
            )}
        >
            <div className={cn(
                "bg-black/80 backdrop-blur-sm border border-gray-700/50 shadow-lg",
                isMobile
                    ? "rounded-full px-3 py-2" // Mobile: horizontal pill
                    : "rounded-full py-3 px-2" // Desktop: vertical pill
            )}>
                <div className={cn(
                    "gap-1",
                    isMobile
                        ? "flex flex-row" // Mobile: horizontal layout
                        : "flex flex-col" // Desktop: vertical layout
                )}>
                    {SECTION_CONFIG.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            // Unreachable by keyboard while faded out, so the tab
                            // order does not run through an invisible control.
                            tabIndex={isVisible ? 0 : -1}
                            className={cn(
                                "relative group rounded-full text-xs font-medium transition-all duration-200",
                                "hover:bg-yellow-500/20 hover:text-yellow-400",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500",
                                isMobile
                                    ? "px-2.5 py-2 text-sm"
                                    : "px-3 py-2",
                                activeSection === section.id
                                    ? "bg-yellow-500/30 text-yellow-400 shadow-md"
                                    : "text-gray-400"
                            )}
                            title={t(section.titleKey)}
                            aria-label={t(section.titleKey)}
                            // "location", not "page": these scroll within the
                            // current document rather than navigating to another
                            // page, which is what "page" asserts.
                            aria-current={activeSection === section.id ? "location" : undefined}
                        >
                            <span className="relative z-10">
                                {isMobile ? section.icon : t(section.titleKey)}
                            </span>
                            {activeSection === section.id && (
                                <div className="absolute inset-0 bg-yellow-500/10 rounded-full border border-yellow-500/30" />
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </nav>
    );
}
